import { useVirtualizer } from '@tanstack/react-virtual'
import { Loader2 } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

/** ビューポート外に追加でレンダリング（画像先読み）する行数 */
const OVERSCAN_ROWS = 10

type Props = {
  /** 各アイテムをレンダリングするコールバック（globalIndex, isImageActive） */
  renderItem: (globalIndex: number, isImageActive: boolean) => React.ReactNode
  /** 初期表示ページ番号（1ベース） */
  initialPage: number
  /** 総ページ数 */
  totalPages: number
  /** 1ページあたりのアイテム数 */
  perPage: number

  /** 全プロダクト数（フラット化後） */
  totalItems: number
  /** グリッド列数 */
  colSize?: number
  /** アスペクト比 */
  aspectRatio?: 'landscape' | 'portrait'
  /** 前ページをロードするコールバック */
  onLoadPrev: () => void
  /** 次ページをロードするコールバック */
  onLoadNext: () => void
  /** 前ページロード中 */
  isLoadingPrev: boolean
  /** 次ページロード中 */
  isLoadingNext: boolean
  /** 最小ロード済みページ */
  minPage: number
  /** 最大ロード済みページ */
  maxPage: number
  /** 現在のアクティブページ番号が変わった時のコールバック */
  onActivePageChange?: (pageNum: number) => void
}

export const InfiniteGridLayout = ({
  renderItem,
  initialPage,
  totalPages,
  perPage,
  totalItems,
  colSize = 3,
  aspectRatio = 'portrait',
  onLoadPrev,
  onLoadNext,
  isLoadingPrev,
  isLoadingNext,
  minPage,
  maxPage,
  onActivePageChange,
}: Props) => {
  'use no memo' // TanStack Virtual は React Compiler と非互換なため memoization をスキップ
  const parentRef = useRef<HTMLDivElement | null>(null)
  const topSentinelRef = useRef<HTMLDivElement | null>(null)
  const bottomSentinelRef = useRef<HTMLDivElement | null>(null)
  const activePageRef = useRef(initialPage)

  const totalRows = Math.ceil(totalItems / colSize)

  // 行高さ計算
  const getRowHeight = useCallback(() => {
    const colWidth = window.innerWidth / colSize
    return Math.round(aspectRatio === 'portrait' ? colWidth * 1.5 : colWidth * (9 / 16))
  }, [colSize, aspectRatio])

  // 現在表示中のページ番号（画像制御に使用）
  const [activePageNum, setActivePageNum] = useState(initialPage)

  // @tanstack/react-virtual でグリッド行を仮想化
  // eslint-disable-next-line react-hooks/incompatible-library -- TanStack Virtual は React Compiler 不対応、上記 'use no memo' で memoize 除外済み
  const rowVirtualizer = useVirtualizer({
    count: totalRows,
    getScrollElement: () => parentRef.current,
    estimateSize: getRowHeight,
    overscan: OVERSCAN_ROWS,
  })

  // リサイズ or aspectRatio/colSize 変更時にサイズを再計測
  useEffect(() => {
    rowVirtualizer.measure()
  }, [rowVirtualizer, aspectRatio, colSize])

  useEffect(() => {
    const update = () => rowVirtualizer.measure()
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [rowVirtualizer])

  // スクロール時にアクティブページを更新
  const handleScroll = useCallback(() => {
    if (!parentRef.current) return
    const st = parentRef.current.scrollTop
    const viewportHeight = parentRef.current.clientHeight
    const rowHeight = getRowHeight()
    if (rowHeight <= 0) return

    const centerY = st + viewportHeight / 2
    const centerRow = Math.floor(centerY / rowHeight)
    const centerItemIndex = centerRow * colSize
    const pageOffset = Math.floor(centerItemIndex / perPage)
    const newActivePage = minPage + pageOffset
    if (newActivePage !== activePageRef.current && newActivePage >= minPage && newActivePage <= maxPage) {
      activePageRef.current = newActivePage
      setActivePageNum(newActivePage)
      onActivePageChange?.(newActivePage)
    }
  }, [getRowHeight, colSize, perPage, minPage, maxPage, onActivePageChange])

  // IntersectionObserver で上端/下端のセンチネルを監視
  useEffect(() => {
    if (!parentRef.current) return

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue
          if (entry.target === topSentinelRef.current && minPage > 1) {
            onLoadPrev()
          }
          if (entry.target === bottomSentinelRef.current && maxPage < totalPages) {
            onLoadNext()
          }
        }
      },
      { root: parentRef.current, rootMargin: '200px' },
    )

    if (topSentinelRef.current) observer.observe(topSentinelRef.current)
    if (bottomSentinelRef.current) observer.observe(bottomSentinelRef.current)

    return () => observer.disconnect()
  }, [minPage, maxPage, totalPages, onLoadPrev, onLoadNext])

  const widthStyle = useMemo(() => {
    if (colSize === 1) return 'w-screen'
    if (colSize === 2) return 'w-1/2'
    if (colSize === 3) return 'w-1/3'
    if (colSize === 4) return 'w-1/4'
    if (colSize === 5) return 'w-1/5'
    return 'w-1/3'
  }, [colSize])

  // 画像アクティブ判定: アイテムのページ番号がactivePageNum ± 1以内か
  const isImageActiveForIndex = useCallback(
    (globalIndex: number): boolean => {
      const pageOffset = Math.floor(globalIndex / perPage)
      const itemPage = minPage + pageOffset
      return Math.abs(itemPage - activePageNum) <= 1
    },
    [perPage, minPage, activePageNum],
  )

  return (
    <div ref={parentRef} className="h-full overflow-y-auto" onScroll={handleScroll}>
      {/* 上端センチネル */}
      <div ref={topSentinelRef} className="h-px" />

      {/* 上方向ローディング */}
      {isLoadingPrev && (
        <div className="flex justify-center py-4">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* 仮想化コンテナ */}
      <div className="relative w-full" style={{ height: rowVirtualizer.getTotalSize() }}>
        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
          const rowIndex = virtualRow.index
          return (
            <div
              key={rowIndex}
              className="absolute left-0 w-full flex"
              style={{ top: virtualRow.start, height: virtualRow.size }}
            >
              {Array.from({ length: colSize }, (_, cellIndex) => {
                const globalIndex = rowIndex * colSize + cellIndex
                return (
                  <div key={cellIndex} className={widthStyle}>
                    {globalIndex < totalItems && renderItem(globalIndex, isImageActiveForIndex(globalIndex))}
                  </div>
                )
              })}
            </div>
          )
        })}
      </div>

      {/* 下方向ローディング */}
      {isLoadingNext && (
        <div className="flex justify-center py-4">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* 下端センチネル */}
      <div ref={bottomSentinelRef} className="h-px" />
    </div>
  )
}
